import * as React from 'react';

import {
  enumLoadStatus,
  enumSalesLoadLineItemPhase,
} from '@rematter/frontend-lib-api-client';
import { cn, ContentCard, Footer, Widget } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai/react';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import { $loadForm, $loadMaterialsStatus } from '../../_state/loadForm.state';
import { $scaleTicketTouchMode } from '../../_state/scaleTicketSettings.state';
import { useWeightUnitManager } from '../../hooks/useWeightUnitManager';
import { LoadFormState, LoadRecord } from '../../types/load';
import { loadHasBeenReceived, loadHasBeenShipped } from '../../utils/load';
import { LoadMaterialsForm } from '../_shared/LoadMaterialsForm';
import { WeightUnitSwitch } from '../_shared/WeightUnitSwitch';

import { LoadFormFooterActions } from './LoadFormFooterActions';
import { LoadMaterialsFooter } from './LoadMaterialsFooter';
import { LoadPhaseToggle } from './LoadPhaseToggle';
import { LoadReconciliationForm } from './LoadReconciliationForm';
import { LoadStaged } from './LoadStaged';

import { TouchModeSwitch } from '~/shared/components/TouchModeSwitch';

type Props = {
  load?: LoadRecord | null;
  isSaving?: boolean;
  onDiscard: (onlyDiscardDatesAndEntities?: boolean) => void;
  onSaveUpdates: (
    tab?: 'load-info' | 'materials',
    data?: LoadFormState,
    onlyDatesAndEntities?: boolean,
  ) => void;
};

const loadMaterialsFormSelector = (state: LoadFormState) => ({
  weightDisplayUnit: state.weightDisplayUnit,
});

export const LoadTabMaterialsInfo: React.FC<Props> = ({
  load,
  isSaving = false,
  onDiscard,
  onSaveUpdates,
}) => {
  const selectedFormAtom = React.useMemo(
    () => selectAtom($loadForm, loadMaterialsFormSelector, isEqual),
    [],
  );
  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($loadForm);

  const loadMaterialsStatus = useAtomValue($loadMaterialsStatus);
  const [touchModeEnabled, setTouchModeEnabled] = useAtom(
    $scaleTicketTouchMode,
  );

  const {
    currentWeightUnit,
    handleWeightUnitChange,
    effectiveWeightDisplayUnit,
  } = useWeightUnitManager({
    weightDisplayUnitPreference:
      form.weightDisplayUnit ?? load?.weightDisplayUnit,
    onWeightDisplayUnitChange: (unit) =>
      updateForm((prev) => ({ ...prev, weightDisplayUnit: unit })),
    defaultUnit: 'PriceUnit',
  });

  const isDisabled =
    load?.status === enumLoadStatus.Voided ||
    load?.status === enumLoadStatus.Reconciled;

  const isLoadShipped = loadHasBeenShipped(load?.status);

  const isStageView = loadMaterialsStatus === enumSalesLoadLineItemPhase.Staged;

  const isRequestOrStageView =
    loadMaterialsStatus === enumSalesLoadLineItemPhase.Requested || isStageView;

  const isLoadReceived = loadHasBeenReceived(load?.status);

  return (
    <div
      className={cn(
        '@LoadTabMaterialsInfo flex flex-col gap-5 overflow-hidden p-5',
        !isStageView && 'gap-0',
      )}
    >
      {!isLoadReceived && (
        <Widget
          className={cn(
            'relative flex w-full flex-shrink-0 items-center justify-between gap-2 px-5 py-4',
            !isStageView && 'rounded-b-none border-b-0 border-neutral/10',
          )}
        >
          {isRequestOrStageView && <LoadPhaseToggle />}
          <div className="flex items-center gap-2">
            {isStageView && (
              <TouchModeSwitch
                touchModeEnabled={touchModeEnabled}
                onChange={setTouchModeEnabled}
              />
            )}
            <WeightUnitSwitch
              value={currentWeightUnit}
              onChange={handleWeightUnitChange}
              showTooltip={isStageView}
            />
          </div>
        </Widget>
      )}

      {isLoadReceived ? (
        <LoadReconciliationForm
          load={load}
          isDisabled={isDisabled}
          onDiscard={onDiscard}
          onSaveUpdates={onSaveUpdates}
          isSaving={isSaving}
        />
      ) : (
        <ContentCard
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-none rounded-b-lg border-t-0 p-0',
            isStageView && 'border-none bg-transparent',
          )}
        >
          {loadMaterialsStatus !== enumSalesLoadLineItemPhase.Staged ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
              <LoadMaterialsForm
                effectiveWeightDisplayUnit={effectiveWeightDisplayUnit}
                disabled={isDisabled || isLoadShipped}
              />
            </div>
          ) : (
            <LoadStaged
              disabled={isDisabled || isLoadShipped}
              weightDisplayUnitPreference={effectiveWeightDisplayUnit}
              onSaveUpdates={onSaveUpdates}
              onDiscard={onDiscard}
              isSaving={isSaving}
            />
          )}

          {!isStageView && (
            <Footer className="@LoadFooter flex-shrink-0" displayShadow>
              <div className="flex items-center justify-end gap-5 px-5 py-2.5">
                <LoadMaterialsFooter
                  weightDisplayUnitPreference={effectiveWeightDisplayUnit}
                />
                {!isDisabled && (
                  <LoadFormFooterActions
                    tab="materials"
                    onSaveUpdates={onSaveUpdates}
                    onDiscard={onDiscard}
                    isSaving={isSaving}
                  />
                )}
              </div>
            </Footer>
          )}
        </ContentCard>
      )}
    </div>
  );
};
